/**
 * E-GTCC-VIEW-TEN1, tab "Rollen".
 *
 * Reference: `GTCC_REFERENCE_2026-09-01.html:733-736` — the panel "Rollen"
 * with one `.row` "Owner · Admin · Fleet-Admin · Dispatcher · Viewer · Driver
 * — Rechte-Matrix →" and the muted-mono sentence underneath. "Einladungen" is
 * its own reference tab (`InvitationsTab.tsx`), not a second column here —
 * B-GTCC-FIX-TEN1 undoes the combined layout E-GTCC-VIEW-TEN1 shipped as a
 * deliberate Owner-order deviation (`ABWEICHUNGEN.md` §E "ten", point 1).
 *
 * That sentence ("Eigene Rollen erst, wenn sie WIRKEN — heute gemessen: noch
 * Dekoration") is not an opinion here, it is the measurement: the member role
 * tag `viewer` maps to no entitlement role at all
 * (`access/roles.ts:210` — `viewer: null`), so a member carrying it receives
 * an empty permission set. The row states that and stays grey.
 *
 * Shared UI Foundation Rule (AGENTS.md): the rights matrix exists as
 * `access/RoleCatalogPage.tsx`, but that component is a route-level page — it
 * owns an `h1` and reads the admin navigation, neither of which a GTCC tab may
 * bring. The matrix here therefore renders locally from the same single source
 * of truth (`BUILT_IN_ROLES` / `permissionsForRole`), so the two surfaces can
 * never disagree. Reason recorded for the sprint closeout.
 */
import { Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import type { ReactNode } from "react";

import {
  BUILT_IN_ROLES,
  MEMBER_ROLE_TO_BUILTIN_ID,
  permissionDescKey,
  permissionsForRole,
  roleLabelKey,
  type Role,
} from "../../../../access/roles.js";
import {
  MONO_STYLE,
  TABLE_CELL_STYLE,
  TABLE_HEAD_STYLE,
  TABLE_STYLE,
  TABLE_WRAP_STYLE,
  TenMutedNote,
  TenPanel,
  TenRow,
  type TenTabProps,
} from "../components/TenPanels.js";

/**
 * Die Matrix zeigt die Tenant-Rollen, die es wirklich gibt — die beiden
 * Plattform-Rollen gehoeren keinem Tenant und stehen darum nicht darin.
 *
 * Die Referenz-Zeile nennt sechs Namen: Owner · Admin · Fleet-Admin ·
 * Dispatcher · Viewer · Driver. Fuenf davon sind gemessene Rollen-IDs; fuer
 * "Viewer" gibt es keine — der Mitglieds-Tag `viewer` zeigt auf keine
 * Entitlement-Rolle (`access/roles.ts:214`). Statt ihn auf `auditor` zu raten,
 * bleibt er aus der Matrix heraus und wird im Satz darunter benannt.
 */
function tenantRoles(): readonly Role[] {
  return (Object.values(BUILT_IN_ROLES) as readonly Role[]).filter(
    (role) => !role.id.startsWith("platform."),
  );
}

function RightsMatrix(): ReactNode {
  const { t } = useLocale();
  const roles = tenantRoles();

  /* Jede Berechtigung, die irgendeine der gezeigten Rollen traegt — einmal. */
  const permissions = [...new Set(roles.flatMap((role) => [...role.permissions]))].sort();

  return (
    <div style={TABLE_WRAP_STYLE}>
      <table style={TABLE_STYLE} data-testid="gtcc-ten-roles-matrix">
        <caption style={{ ...MONO_STYLE, textAlign: "start" }}>
          {t("admin.gtcc.ten.roles.matrix_caption")}
        </caption>
        <thead>
          <tr>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.ten.roles.permission_column")}
            </th>
            {roles.map((role) => (
              <th key={role.id} scope="col" style={TABLE_HEAD_STYLE}>
                {t(roleLabelKey(role.id))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {permissions.map((permission) => (
            <tr key={permission} data-testid={`gtcc-ten-roles-permission-${permission}`}>
              {/* Erste Spalte als Zelle — wie in allen GTCC-Tabellen. */}
              <td style={TABLE_CELL_STYLE}>{t(permissionDescKey(permission))}</td>
              {roles.map((role) => {
                const granted = permissionsForRole(role.id).includes(permission);
                return (
                  <td
                    key={role.id}
                    style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}
                    data-testid={`gtcc-ten-roles-cell-${role.id}-${permission}`}
                  >
                    {/*
                     * Wort statt Haken: ein Zeichen allein sagt in der
                     * Vorlesereihenfolge nichts, und eine verborgene
                     * Zweitbeschriftung braucht es dann auch nicht.
                     */}
                    {granted ? t("admin.gtcc.ten.roles.granted") : t("admin.gtcc.ten.roles.denied")}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RolesTab(_props: TenTabProps): ReactNode {
  const { t } = useLocale();

  /*
   * Gemessen, nicht behauptet: welche Mitglieds-Rollen ueberhaupt auf eine
   * Entitlement-Rolle zeigen. `viewer` zeigt auf nichts — genau das ist der
   * Satz der Referenz.
   */
  const decorativeTags = Object.entries(MEMBER_ROLE_TO_BUILTIN_ID)
    .filter(([, builtIn]) => builtIn === null)
    .map(([tag]) => tag);

  return (
    <TenPanel testId="gtcc-ten-roles" title={t("admin.gtcc.ten.roles.panel")}>
      <Stack gap={2}>
        <TenRow
          testId="gtcc-ten-roles-list"
          tone="muted"
          label={t("admin.gtcc.ten.roles.row_label")}
          value={t("admin.gtcc.ten.roles.row_value")}
        >
          <RightsMatrix />
        </TenRow>
        <TenMutedNote testId="gtcc-ten-roles-note">
          {decorativeTags.length === 0
            ? t("admin.gtcc.ten.roles.note_effective")
            : t("admin.gtcc.ten.roles.note_decorative", {
                tags: decorativeTags.join(", "),
              })}
        </TenMutedNote>
      </Stack>
    </TenPanel>
  );
}
